import { FormikProvider, useFormik } from 'formik';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '../../components/UI';
import { Input } from '../../components/form';
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useAuth } from '../../context/Auth';
import { emailValidator } from '../../components/form/Input/validators/email-validator';
import { notEmpty } from '../../components/form/Input/validators/string-validators';
import { CustomError } from '../../helpers/CustomError';
import SignInWithGoogle from './SignInWithGoogle';
import SignInWithTwitter from './SignInWithTwitter';
import { useSignInContext } from './SignInContext';
import { trackEvent } from '../../helpers/analytics';
import { logger } from '../../helpers/logger';

const SignIn = () => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const { t } = useTranslation();

  const [signInError, setSignInError] = useState('');
  const { state, dispatch } = useSignInContext();

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    onSubmit: (values) => {
      const { email, password } = values;

      trackEvent('login_initiated');

      dispatch({
        type: 'SET_EMAIL',
        payload: email,
      });

      setSignInError('');
      signIn(email, password)
        .then((cognitoUser) => {
          // logger.log('login', cognitoUser);

          dispatch({
            type: 'SET_COGNITO_USER',
            payload: cognitoUser,
          });

          if (
            cognitoUser.challengeName === 'SMS_MFA' ||
            cognitoUser.challengeName === 'SOFTWARE_TOKEN_MFA'
          ) {
            dispatch({
              type: 'SET_STEP',
              payload: '2FA',
            });
          }

          if (
            cognitoUser?.attributes &&
            !cognitoUser?.attributes?.email_verified
          ) {
            throw new CustomError(
              'EmailNotConfirmedException',
              'Email changed to be confirmed'
            );
          }

          dispatch({
            type: 'SET_LOGGED',
            payload: true,
          });

          //formik.setSubmitting(false);

          return cognitoUser;
        })
        .catch((e) => {
          logger.error('loginError', e.code);
          formik.setSubmitting(false);

          dispatch({
            type: 'SET_PASSWORD',
            payload: password,
          });

          if (e.code === 'UserNotConfirmedException') {
            dispatch({
              type: 'SET_STEP',
              payload: 'CONFIRM_EMAIL',
            });
          }

          if (e.code === 'PasswordResetRequiredException') {
            navigate(`/forgot-password`, { state: { email } });
          }

          if (e.code === 'EmailNotConfirmedException') {
            dispatch({
              type: 'SET_STEP',
              payload: 'CONFIRM_EMAIL_CHANGE',
            });
          }

          setSignInError(
            t(`signIn.form.error.${e.code}`, { defaultValue: e.message })
          );
        });
    },
    validateOnMount: true,
  });

  return (
    <>
      <h1 className="text-black text-xl font-semibold text-center dark:text-white">
        {t('signIn.form.title')}
      </h1>

      <SignInWithGoogle />
      <SignInWithTwitter />

      <div className="mt-8 w-full flex items-center">
        <div className="border-b-1 border-grey-30 dark:border-grey-70 flex-grow-3"></div>
        <div className="flex-grow-1 flex justify-center text-grey-30 dark:border-grey-70 text-sm uppercase">
          {t('signIn.form.or')}
        </div>
        <div className="border-b-1 border-grey-30 dark:border-grey-70 flex-grow-3"></div>
      </div>
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit} className="w-full">
          <div className="mt-8">
            <Input
              name="email"
              formik={formik}
              id="email"
              validate={emailValidator}
              label={t('signIn.form.label.email')}
              size="full"
              type="text"
              placeholder={t('signIn.form.placeholder.email')}
              errorMessage={
                formik.errors.email && formik.touched.email
                  ? t('signIn.form.error.email')
                  : ''
              }
            />
          </div>
          <div className="mt-10">
            <Input
              name="password"
              formik={formik}
              id="password"
              validate={notEmpty}
              label={t('signIn.form.label.password')}
              size="full"
              type="password"
              placeholder={t('signIn.form.placeholder.password')}
              errorMessage={
                formik.errors.password && formik.touched.password
                  ? t('signIn.form.error.password')
                  : signInError || ''
              }
            />
            <div className="flex mt-10 justify-center">
              <button
                className="text-sm text-black font-semibold dark:text-white"
                onClick={() => {
                  navigate(`/forgot-password`);
                }}
                type={'button'}
              >
                {t('signIn.form.forgotPassword')}
              </button>
            </div>
          </div>
          <div className="mt-5">
            <Button
              type="submit"
              variant="primary"
              size="full"
              buttonClass="w-64"
              loading={formik.isSubmitting || state.isLoading}
            >
              {t('signIn.form.label.submit')}
            </Button>
            <div className="mt-4 text-center dark:text-grey-30">
              <Trans i18nKey="signIn.form.notHaveAccount">
                don't have
                <Link
                  className={'underline dark:text-white font-semibold'}
                  to={'/signup'}
                >
                  login
                </Link>
              </Trans>
            </div>
          </div>
        </form>
      </FormikProvider>
    </>
  );
};

export default SignIn;
